import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Check } from 'lucide-react';
import { memberAuth, memberPortal, useAsync } from '@fitboxx/shared';
import type { Member } from '@fitboxx/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface WaiverStatus {
  requires_signing: boolean;
  waiver?: {
    title?: string; version?: number; summary?: string;
    body_markdown?: string; governing_law?: string;
  };
}

export default function Agreements() {
  const me = useAsync(() => memberAuth.me() as Promise<Member>);
  const { data, loading, refresh } = useAsync(
    () => memberPortal.waiverStatus() as Promise<WaiverStatus>,
  );
  const [typedName, setTypedName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function sign() {
    setBusy(true); setErr(null);
    try {
      await memberPortal.signWaiver({
        typed_name: typedName || me.data?.full_name || ''
      });
      refresh();
    } catch (e) { setErr((e as Error).message || 'Could not sign.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/me"><ArrowLeft className="h-4 w-4 mr-1" />My account</Link>
        </Button>
        <h1 className="text-xl font-semibold tracking-tight">Agreements</h1>
      </div>

      {loading ? <Skeleton className="h-32 w-full" /> :
        !data?.waiver ? (
          <Card className="p-8 text-center text-sm text-muted-foreground">
            <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
            No agreements published yet.
          </Card>
        ) : !data.requires_signing ? (
          <Card className="p-5">
            <div className="flex items-center gap-2 text-emerald-700 font-medium">
              <Check className="h-5 w-5" /> {data.waiver.title} signed
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              v{data.waiver.version} · You're all set.
            </div>
          </Card>
        ) : (
          <Card className="p-5 space-y-4">
            <div>
              <div className="text-lg font-semibold">{data.waiver.title}</div>
              <div className="text-xs text-muted-foreground">
                v{data.waiver.version}
                {data.waiver.governing_law && ` · governed by ${data.waiver.governing_law}`}
              </div>
              {data.waiver.summary && (
                <div className="text-sm mt-2">{data.waiver.summary}</div>
              )}
            </div>

            {data.waiver.body_markdown && (
              <div className="max-h-72 overflow-y-auto rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap">
                {data.waiver.body_markdown}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="typed-name">Type your full name to sign</Label>
              <Input id="typed-name" value={typedName}
                     onChange={(e) => setTypedName(e.target.value)}
                     placeholder={me.data?.full_name || 'Your name'} />
            </div>

            {err && <div className="text-sm text-destructive">{err}</div>}

            <Button onClick={() => void sign()} disabled={busy} className="w-full">
              {busy ? 'Signing…' : 'I agree and sign'}
            </Button>
          </Card>
        )
      }
    </div>
  );
}
